import React from "react";
import { Link, graphql } from "gatsby";
import { ChevronDown, BookOpenText } from "lucide-react";
import { getImage, StaticImage } from "gatsby-plugin-image";

import Layout from "../components/layout";
import Seo from "../components/seo";
import BlogCard from "../components/blog-card";
import Sparkle from "../components/sparkle";
import Sparkle2 from "../components/sparkle-2";
import PageTitle from "../components/page-title";
import NewsCard from "../components/news-card";

const IndexPage = ({ data }) => {
  return (
    <Layout>
      <section className="w-screen flex flex-col justify-between md:items-start items-between overflow-hidden">
        <main className="relative h-full z-10 w-full md:px-0 px-6 flex flex-col items-center justify-center space-y-8 max-w-4xl mx-auto my-8 md:my-16">
          <div className="relative w-full mx-auto flex sm:flex-row flex-col justify-center items-start sm:items-center">
            <Sparkle />
            <h1 className="font-header text-4xl sm:text-5xl sm:pt-20 lg:pt-5 md:text-6xl lg:text-7xl font-bold tracking-tighter w-full inline-block text-left md:text-center relative">
              A User's Guide to
              <br className="sm:block hidden" />{" "}
              <span className="header-gradient">Artificial Intelligence</span>
            </h1>
            <Sparkle2 />
          </div>
          <p className="max-w-3xl text-grey-800 md:text-center sm:text-lg">
            Stay up to date with the cutting-edge of AI.
          </p>

          <div className="text-center text-grey-800">
            <ChevronDown className="md:mt-8 w-8 md:w-12 h-auto rounded-full border-grey-700 border-2 p-1" />
          </div>

          <div className="relative w-screen h-32 border-b-[3px] border-black">
            <StaticImage
              src="../../data/images/cover.png"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent"></div>
          </div>
        </main>
        <div className="max-w-4xl mx-auto tracking-tighter px-8">
          <h2 className="text-xl font-bold leading-none text-black md:text-2xl lg:text-3xl">
            From foundational concepts and terminology to in-depth guides on
            various AI tools and techniques, we curate a comprehensive resource
            for anyone looking to navigate the world of artificial intelligence.
          </h2>
        </div>

        <div className="px-4 md:px-10 mx-auto max-w-7xl my-8 md:my-16">
          <PageTitle title="Your Complete Guide to AI" />
          <div className="grid grid-cols-12 gap-8 mt-12">
            <BlogCard
              title="AI Glossary"
              subtitle="Your AI Dictionary"
              info="AI concepts in clear, easy-to-understand language."
              to="/glossary"
              image={getImage(data.glossary)}
            />
            <BlogCard
              title="AI Tools"
              subtitle="Under Construction"
              info="A curated list of cutting-edge AI tools."
              to="#"
              className="hidden lg:block"
              image={getImage(data.tools)}
            />
            <BlogCard
              title="Blog"
              subtitle="Updated Daily"
              info="News and research at the forefront of AI."
              to="/blog"
              image={getImage(data.blog)}
            />

            <BlogCard
              title={
                <>
                  A User's Guide to
                  <br />
                  <span className="header-gradient">Midjourney</span>
                </>
              }
              subtitle="Under Construction"
              to="/blog/generative-ai/getting-started-with-midjourney-v6-2024-05-15/"
              category="Image Generation"
              image={getImage(data.midjourney)}
              size="lg"
            />

            <BlogCard
              title="How to Prompt Large Language Models (LLMs)"
              info="Unlock the power of large language models with our practical prompting guide."
              to="/blog/ai-user's-guide/mastering-prompt-engineering-with-openai-api-strategies-and-techniques-2024-05-15/"
              category="Language Models"
              image={getImage(data.llm)}
              size="md"
            />

            <div className="col-span-12 md:col-span-6 relative flex flex-col items-center justify-center flex-1 py-12 md:py-32 overflow-hidden text-center bg-white border-2 border-black md:mt-0 hover:shadow-xl duration-150 ease-out transform">
              <BookOpenText className="mr-2 w-24 h-auto" />
              <div className="flex flex-col px-10 space-y-6 md:px-8 mt-2">
                <h3 className="text-2xl font-bold leading-tight md:text-3xl text-grey-900">
                  Our Mission: Demystifying AI
                </h3>
                <p className="text-xl font-normal text-grey-700">
                  Explore the forefront of Artificial Intelligence with User's
                  Guide to AI. From the latest news and research to expert tips
                  on LLM and image generation prompting, we provide the insights
                  and tools you need to advance your AI projects, whether you're
                  a beginner or a seasoned professional.
                </p>
                <Link
                  to="/mission"
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                >
                  Read More
                  <svg
                    className="ml-2 -mr-0.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    ></path>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="mx-auto mb-12 pt-4">
          <PageTitle title="AI News" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8 mx-4">
            {data.news.nodes.map((node, index) => (
              <div
                className={`h-full col-span-${
                  index % 4 === 2 || index % 4 === 3 ? 2 : 1
                }`}
              >
                <NewsCard node={node} key={index} className="h-full" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export const Head = ({ data }) => {
  const { title, description } = data.site.siteMetadata;
  return <Seo title={`${title} | ${description}`} />;
};

export default IndexPage;

export const pageQuery = graphql`
  query {
    site {
      siteMetadata {
        title
        description
      }
    }
    midjourney: file(relativePath: { eq: "midjourney-guide.jpg" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    blog: file(relativePath: { eq: "blog.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    glossary: file(relativePath: { eq: "glossary.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    tools: file(relativePath: { eq: "tools.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    llm: file(relativePath: { eq: "prompt-llm.png" }) {
      childImageSharp {
        gatsbyImageData
      }
    }
    news: allMarkdownRemark(
      filter: { fields: { collection: { eq: "news" } } }
      sort: { frontmatter: { date: DESC } }
      limit: 4
    ) {
      nodes {
        frontmatter {
          title
          category
          summary
          date(formatString: "DD MMMM YYYY")
        }
        fields {
          slug
        }
      }
    }
  }
`;
